import { type RouterOutput, trpc } from '@/config/trpc'
import { Button } from '@/components/ui/shadcn/button'
import { Card, CardContent, CardTitle } from '@/components/ui/shadcn/card'
import { Edit, TrashIcon } from 'lucide-react'
import { Fragment, useMemo } from 'react'
import { useNavigate } from 'react-router'

type Campaign = RouterOutput['campaign']['list'][number]

export default function CampaignsListPage() {
  const navigate = useNavigate()

  const isLoading = false

  // trpc
  const utils = trpc.useUtils()

  const { data: campaigns } = trpc.campaign.list.useQuery()
  const campaignsByDay = useMemo(() => (campaigns ? byDay(campaigns) : undefined), [campaigns])

  const { mutateAsync: createCampaign } = trpc.campaign.create.useMutation()
  const { mutateAsync: deleteCampaign } = trpc.campaign.delete.useMutation()

  // handlers

  const handleCreate = async () => {
    const { id } = await createCampaign({ title: 'New Campaign' })
    navigate(`/campaign/${id}`)
    utils.campaign.list.invalidate()
  }

  const handleEdit = (id: number) => {
    navigate(`/campaign/${id}`)
  }

  const handleDelete = async (id: number) => {
    await deleteCampaign({ id })
    utils.campaign.list.invalidate()
  }

  return (
    <section className="mt-[3vh] flex w-full flex-1 flex-col items-center justify-center p-6">
      <div className="text-dark-primary mt-6 flex w-full max-w-[790px] flex-col gap-2">
        <div className="mb-4 flex justify-between">
          <div>
            <h1 className="text-2xl font-bold">My Campaigns</h1>
            <p className="text-base text-gray-400">Your campaigns</p>
          </div>
          <Button onClick={handleCreate} variant={'primary'}>
            Create a new campaign
          </Button>
        </div>

        <div className="grid w-full grid-cols-1 gap-2">
          {isLoading && (
            <Card className="animate-pulse p-4">
              <CardContent className="flex flex-col">
                <div className="mb-2 h-4 rounded-sm bg-stone-300" />
                <div className="h-3 rounded-sm bg-stone-300" />
              </CardContent>
            </Card>
          )}

          {campaignsByDay &&
            Object.entries(campaignsByDay).map(([date, campaigns]) => (
              <Fragment key={date}>
                <div className="flex justify-between text-xl">
                  <h2>
                    {dayFromDate(date)}, {formatDisplayDate(date)}
                  </h2>
                  <h2>
                    {monthFromDate(date)} {new Date(date).getFullYear()}
                  </h2>
                </div>

                {campaigns.map((campaign) => {
                  return (
                    <Card key={campaign.id} className="flex flex-col space-y-4 p-3 sm:p-3">
                      <CardTitle className="flex items-center justify-between">
                        <CardHeader campaign={campaign} />

                        <div className="flex items-center gap-2">
                          <Button onClick={() => handleEdit(campaign.id)} size="icon">
                            <Edit className="text-white" />
                          </Button>
                          <Button
                            onClick={() => handleDelete(campaign.id)}
                            size="icon"
                            className="bg-red-500">
                            <TrashIcon className="text-white" />
                          </Button>
                        </div>
                      </CardTitle>
                      <CardContent className="flex flex-col">
                        <div className="flex flex-col gap-2">
                          <p className="text-dark-light overflow-hidden truncate whitespace-pre-wrap text-sm">
                            {campaign.description}
                          </p>
                          <p className="text-dark-light overflow-hidden truncate whitespace-pre-wrap text-sm">
                            {campaign.story}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  )
                })}
              </Fragment>
            ))}

          {campaigns && campaigns.length === 0 && (
            <Card>
              <CardContent>
                <p className="">You don't have any campaigns yet...</p>
                <p>
                  <Button onClick={handleCreate} variant={'outline'} className="p-0 underline">
                    Create a new campaign
                  </Button>{' '}
                  and get started!
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </section>
  )
}

const CardHeader = ({ campaign }: { campaign: Campaign }) => {
  return (
    <div>
      <div className="flex items-center space-x-2">
        <p className="text-dark-primary text-nowrap text-lg font-bold">{campaign.title}</p>

        <span className="opacity-70">•</span>
        <span className="w-full font-medium">
          {new Date(campaign.startDate).toLocaleDateString(undefined, {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          })}{' '}
          -{' '}
          {new Date(campaign.endDate).toLocaleDateString(undefined, {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          })}
        </span>
      </div>

      <span className="text-sm font-normal text-gray-500">{campaign.goal}</span>
    </div>
  )
}

const byDay = (campaigns: Campaign[]) => {
  return campaigns.reduce(
    (acc, campaign) => {
      const date = new Date(campaign.startDate).toISOString().split('T')[0]!
      if (!acc[date]) {
        acc[date] = []
      }
      acc[date].push(campaign)
      acc[date].sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime())
      return acc
    },
    {} as Record<string, Campaign[]>,
  )
}

const dayFromDate = (date: Date | string) => {
  const day = new Date(date).getDay()
  if (day === 0) return 'Sunday'
  if (day === 1) return 'Monday'
  if (day === 2) return 'Tuesday'
  if (day === 3) return 'Wednesday'
  if (day === 4) return 'Thursday'
  if (day === 5) return 'Friday'
  if (day === 6) return 'Saturday'
  return ''
}

const monthFromDate = (date: Date | string) => {
  const month = new Date(date).getMonth()
  if (month === 0) return 'January'
  if (month === 1) return 'February'
  if (month === 2) return 'March'
  if (month === 3) return 'April'
  if (month === 4) return 'May'
  if (month === 5) return 'June'
  if (month === 6) return 'July'
  if (month === 7) return 'August'
  if (month === 8) return 'September'
  if (month === 9) return 'October'
  if (month === 10) return 'November'
  if (month === 11) return 'December'
  return ''
}

const formatDisplayDate = (dateString: string) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  })
}
